import { useEffect, useMemo, useRef, useState } from "react";
import { SuperAdminSkip } from "@/components/site/SuperAdminSkip";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { PhoneField, TextField, TextareaField } from "@/components/site/fields";
import { Checkbox } from "@/components/ui/checkbox";
import { isValidPhone } from "@/lib/phone";
import { nextVisitSlots } from "@/lib/schoolYear";
import { reportFormError } from "@/lib/form-feedback";
import { submitContactMessage } from "@/lib/contact.functions";
import { isNetworkError, networkErrorMessage, withRetry } from "@/lib/submit-retry";
import { contactSchemaForLocale } from "@/lib/contact-schema";
import type { Locale } from "@/lib/i18n";
import { visitorFields } from "@/lib/analytics-client";
import { useFormPersist } from "@/lib/use-form-persist";

const DATE_LOCALE: Record<Locale, string> = { sv: "sv-SE", en: "en-GB", es: "es-ES" };
const TIME_LABEL: Record<Locale, string> = {
  sv: "kl. 14:00–14:45",
  en: "2:00–2:45 pm",
  es: "14:00–14:45",
};

const TEXT = {
  sv: {
    heading: "Boka ett besök",
    intro:
      "Fyll i några rader så hör vi av oss med en tid som passar. Inget bindande – ni bestämmer själva om ni vill söka sedan.",
    name: "Ditt namn",
    email: "E-post",
    phone: "Telefon",
    child: "Barnets ålder eller årskurs",
    childHint: "T.ex. ”4 år” eller ”åk 3”.",
    when: "Välj en tid",
    whenHint: "Alla besök är kl. 14:00–14:45 (45 minuter). Vi bekräftar tiden per mejl.",
    digital: "Digitalt möte i stället",
    other: "Ingen av tiderna passar – föreslå en annan tid",
    start: "Önskad skolstart",
    startHint: "Frivilligt, t.ex. ”hösten 2027” eller ”så snart som möjligt”.",
    message: "Något vi bör veta?",
    send: "Skicka förfrågan",
    sending: "Skickar…",
    done: "Tack! Vi hör av oss med en tid.",
    doneTitle: "Tack – vi har tagit emot din förfrågan",
    doneBody:
      "Vi hör av oss per mejl med förslag på tid. Behöver du ändra något är det bara att svara på mejlet eller ringa oss.",
    doneNext: "Vi svarar normalt inom en arbetsdag, helgfria vardagar.",
    doneSummary: "Så här läste vi din förfrågan",
    doneAgain: "Skicka en till förfrågan",
    doneContact: "Frågor under tiden? Ring +34 607 148 331 eller mejla ",

    error: "Något gick fel. Försök igen eller mejla oss direkt.",
    errName: "Fyll i ditt namn.",
    errEmail: "Fyll i en giltig e-postadress.",
    errPhone: "Fyll i ett giltigt telefonnummer med landskod.",
    subject: "Bokning av besök",
    note: "Vi svarar normalt inom en arbetsdag.",
  },
  en: {
    heading: "Book a visit",
    intro:
      "Tell us a little and we'll get back with a time that suits you. No commitment – applying later is entirely up to you.",
    name: "Your name",
    email: "Email",
    phone: "Phone",
    child: "Child's age or grade",
    childHint: "E.g. “4 years” or “grade 3”.",
    when: "Choose a time",
    whenHint: "All visits are 2:00–2:45 pm (45 minutes). We confirm the time by email.",
    digital: "Online meeting instead",
    other: "None of these work – suggest another time",
    start: "Preferred start",
    startHint: "Optional, e.g. “autumn 2027” or “as soon as possible”.",
    message: "Anything we should know?",
    send: "Send request",
    sending: "Sending…",
    done: "Thank you! We'll get back to you with a time.",
    doneTitle: "Thank you – we've received your request",
    doneBody:
      "We'll email you with suggested times. If anything needs changing, just reply to that email or give us a call.",
    doneNext: "We normally reply within one working day.",
    doneSummary: "This is what you sent us",
    doneAgain: "Send another request",
    doneContact: "Questions in the meantime? Call +34 607 148 331 or email ",

    error: "Something went wrong. Please try again or email us directly.",
    errName: "Please enter your name.",
    errEmail: "Please enter a valid email address.",
    errPhone: "Enter a valid phone number including country code.",
    subject: "Visit request",
    note: "We normally reply within one working day.",
  },
  es: {
    heading: "Reservar una visita",
    intro:
      "Cuéntanos un poco y te propondremos una hora que te venga bien. Sin compromiso: solicitar plaza después es decisión vuestra.",
    name: "Tu nombre",
    email: "Correo electrónico",
    phone: "Teléfono",
    child: "Edad o curso del niño",
    childHint: "Por ejemplo «4 años» o «3.º de primaria».",
    when: "Elige una hora",
    whenHint: "Todas las visitas son de 14:00 a 14:45 (45 minutos). Confirmamos la hora por correo.",
    digital: "Reunión online en su lugar",
    other: "Ninguna hora me viene bien: proponed otra",
    start: "Inicio deseado",
    startHint: "Opcional, por ejemplo «otoño de 2027» o «lo antes posible».",
    message: "¿Algo que debamos saber?",
    send: "Enviar solicitud",
    sending: "Enviando…",
    done: "¡Gracias! Te propondremos una hora.",
    doneTitle: "Gracias: hemos recibido tu solicitud",
    doneBody:
      "Te escribiremos por correo con una propuesta de hora. Si necesitas cambiar algo, responde a ese correo o llámanos.",
    doneNext: "Normalmente respondemos en un día laborable.",
    doneSummary: "Esto es lo que nos has enviado",
    doneAgain: "Enviar otra solicitud",
    doneContact: "¿Dudas mientras tanto? Llama al +34 607 148 331 o escribe a ",

    error: "Algo ha fallado. Inténtalo de nuevo o escríbenos directamente.",
    errName: "Introduce tu nombre.",
    errEmail: "Introduce un correo electrónico válido.",
    errPhone: "Escribe un teléfono válido con prefijo de país.",
    subject: "Solicitud de visita",
    note: "Normalmente respondemos en un día laborable.",
  },
} as const;

/** Lågtröskel-formulär: boka ett besök utan att fylla i hela anmälan. */
export function VisitForm({ locale }: { locale: Locale }) {
  const t = TEXT[locale];
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const submit = useServerFn(submitContactMessage);
  const openedAt = useRef(Date.now());
  const [sent, setSent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    child: "",
    when: "",
    start: "",
    message: "",
    company: "",
  });

  const clearDraft = useFormPersist(
    "visit",
    { ...form, company: "" },
    (saved) => setForm((prev) => ({ ...prev, ...saved, company: "" })),
  );

  // Räknas fram efter hydrering så server och klient inte får olika datum.
  const [slots, setSlots] = useState<string[]>([]);
  useEffect(() => {
    setSlots(nextVisitSlots(new Date().toISOString().slice(0, 10), 3));
  }, []);

  const slotOptions = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(DATE_LOCALE[locale], {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });
    return slots.map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      const label = fmt.format(new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)));
      return {
        value: `${label.charAt(0).toUpperCase()}${label.slice(1)}, ${TIME_LABEL[locale]}`,
        iso,
      };
    });
  }, [slots, locale]);

  const errors = useMemo(() => {
    const e: { name?: string; email?: string; phone?: string } = {};
    if (!form.name.trim()) e.name = t.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = t.errEmail;
    if (form.phone.trim() && !isValidPhone(form.phone)) e.phone = t.errPhone;
    return e;
  }, [form.name, form.email, form.phone, t]);

  const mutation = useMutation({
    mutationFn: () => {
      // Valen sparas strukturerat; meddelandet innehåller bara familjens egen text.
      const body = form.message.trim() || `${t.when}: ${form.when || "-"}`;
      // Tiden sparas som ett val (datum eller typ), inte som text på familjens språk,
      // så admin alltid kan visa den på svenska utan översättning.
      const chosenSlot = slotOptions.find((s) => s.value === form.when);
      const whenKind = chosenSlot
        ? ("slot" as const)
        : form.when === t.digital
          ? ("digital" as const)
          : form.when === t.other
            ? ("other" as const)
            : ("" as const);
      const payload = contactSchemaForLocale(locale).parse({
        formAnswers: {
          child: form.child,
          when: form.when,
          start: form.start,
          whenIso: chosenSlot?.iso ?? "",
          whenKind,
        },

        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: t.subject,
        message: body,
        company: form.company,
        locale,
        sourcePath: pathname,
        elapsedMs: Date.now() - openedAt.current,
        ...visitorFields(),
      });
      return withRetry(() => submit({ data: payload }));
    },
    onSuccess: () => {
      setSent(true);
      clearDraft();
      toast.success(t.done, { id: "visit-form" });
    },
    onError: (e: Error) =>
      reportFormError(isNetworkError(e) ? networkErrorMessage(locale) : e.message || t.error, {
        toastId: "visit-form",
      }),
  });

  const doneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sent) doneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sent]);

  if (sent) {
    const rows = [
      { label: t.name, value: form.name },
      { label: t.email, value: form.email },
      { label: t.phone, value: form.phone },
      { label: t.child, value: form.child },
      { label: t.when, value: form.when },
      { label: t.start, value: form.start },
    ].filter((r) => r.value.trim());
    return (
      <div ref={doneRef} className="scroll-mt-28 rounded-xl border border-primary/30 bg-primary/5 p-8">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-primary">
          <CheckCircle2 className="size-5 shrink-0" aria-hidden="true" />
          {t.doneTitle}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.doneBody}</p>
        {rows.length ? (
          <div className="mt-5 rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-medium">{t.doneSummary}</p>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              {rows.map((r) => (
                <div key={r.label}>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">{r.label}</dt>
                  <dd className="mt-0.5">{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.doneNext}</p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t.doneContact}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>
          .
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex min-h-11 items-center rounded-md border border-border bg-card px-4 text-sm font-medium transition-colors hover:bg-background"
            onClick={() => {
              setSent(false);
              setSubmitted(false);
              openedAt.current = Date.now();
              setForm({
                name: "",
                email: "",
                phone: "",
                child: "",
                when: "",
                start: "",
                message: "",
                company: "",
              });
            }}
          >
            {t.doneAgain}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      noValidate
      id="boka"
      className="rounded-xl border border-border bg-card p-6 shadow-soft"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        const order = ["name", "email", "phone"] as const;
        const first = order.find((k) => errors[k]);
        if (first) {
          reportFormError(errors[first] as string, {
            anchorId: `visit-${first}`,
            toastId: "visit-form",
          });
          return;
        }
        mutation.mutate();
      }}

    >

      <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
        <CalendarCheck className="size-5 text-primary" aria-hidden="true" />
        {t.heading}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t.intro}</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <TextField
          label={t.name}
          required
          anchorId="visit-name"
          autoComplete="name"
          value={form.name}
          error={submitted ? errors.name : undefined}
          onChange={(v) => setForm({ ...form, name: v })}
        />
        <TextField
          label={t.email}
          required
          anchorId="visit-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={form.email}
          error={submitted ? errors.email : undefined}
          onChange={(v) => setForm({ ...form, email: v })}
        />
        <PhoneField
          label={t.phone}
          anchorId="visit-phone"
          value={form.phone}
          error={submitted ? errors.phone : undefined}
          onChange={(v) => setForm({ ...form, phone: v })}
        />

        <TextField
          label={t.child}
          hint={t.childHint}
          value={form.child}
          onChange={(v) => setForm({ ...form, child: v })}
        />
        <TextField
          label={t.start}
          hint={t.startHint}
          value={form.start}
          onChange={(v) => setForm({ ...form, start: v })}
        />
      </div>

      <fieldset className="mt-5">
        <legend className="text-sm font-medium">{t.when}</legend>
        <p className="mt-1 text-sm text-muted-foreground">{t.whenHint}</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {slotOptions.map((slot) => (
            <label
              key={slot.iso}
              className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                form.when === slot.value
                  ? "border-primary bg-primary/5 font-medium text-primary"
                  : "border-border hover:bg-background"
              }`}
            >
              <Checkbox
                checked={form.when === slot.value}
                onCheckedChange={() => setForm({ ...form, when: form.when === slot.value ? "" : slot.value })}
              />
              <span>{slot.value}</span>
            </label>
          ))}
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {[t.digital, t.other].map((option) => (
            <label
              key={option}
              className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                form.when === option
                  ? "border-primary bg-primary/5 font-medium text-primary"
                  : "border-border hover:bg-background"
              }`}
            >
              <Checkbox
                checked={form.when === option}
                onCheckedChange={() => setForm({ ...form, when: form.when === option ? "" : option })}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-4">
        <TextareaField
          label={t.message}
          rows={4}
          value={form.message}
          onChange={(v) => setForm({ ...form, message: v })}
        />
      </div>

      <div aria-hidden className="hidden">
        <label>
          Company
          <input
            tabIndex={-1}
            autoComplete="off"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </label>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {mutation.isPending ? t.sending : t.send}
        </button>
        <SuperAdminSkip onSkip={() => setSent(true)} />
        <p className="text-sm text-muted-foreground">{t.note}</p>
      </div>
    </form>
  );
}
